import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';
import { analyzeProductDevelopmentPath } from '../../utils/openaiService';

const ProductAnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [productData, setProductData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [tableExists, setTableExists] = useState(true);
  
  // Form states for additional analysis parameters (optional)
  const [targetMarket, setTargetMarket] = useState('Global');
  const [competitorAnalysisNotes, setCompetitorAnalysisNotes] = useState('');
  
  // Target market options
  const marketOptions = [
    'Global',
    'US',
    'EU',
    'EU + UK',
    'Japan',
    'China',
    'South Korea'
  ];

  // Check if product_analysis table exists and create it if it doesn't
  const checkAndCreateTable = async () => {
    try {
      // Try to query the product_analysis table
      const { error } = await supabase
        .from('product_analysis')
        .select('id')
        .limit(1);
      
      // If we get a 404 error, the table doesn't exist
      if (error && error.code === '42P01') {
        setTableExists(false);
        return false;
      }
      
      return true;
    } catch (err) {
      console.error('Error checking product_analysis table:', err);
      setTableExists(false);
      return false;
    }
  };
  
  // Fetch product data
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setIsLoading(true);
        
        // Check if product_analysis table exists
        const exists = await checkAndCreateTable();
        if (!exists) {
          // Show message to user but continue with the rest of the operation
          console.warn('product_analysis table does not exist in the database');
        }
        
        // Fetch product data
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();
        
        if (productError) throw productError;
        setProductData(product);
        
        // Only check for existing analysis if the table exists
        if (exists) {
          // Check if analysis already exists
          const { data: analysis, error: analysisError } = await supabase
            .from('product_analysis')
            .select('*')
            .eq('product_id', id)
            .maybeSingle();
          
          if (!analysisError && analysis) {
            setAnalysisResult(analysis);
          }
        }
        
      } catch (err) {
        console.error('Error fetching product data:', err);
        setError('Failed to load product data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user && id) {
      fetchProductData();
    }
  }, [user, id]);
  
  // Handle analysis submission
  const handleAnalyzeClick = async () => {
    try {
      setIsAnalyzing(true);
      setError(null);
      
      // Check for existing analysis
      const { data: existingAnalysis, error: existingError } = await supabase
        .from('product_analysis')
        .select('id, created_at')
        .eq('product_id', id)
        .maybeSingle();
      
      if (existingError && existingError.code !== 'PGRST116') {
        throw existingError;
      }
      
      if (!tableExists) {
        setError('The product_analysis table does not exist in your database. Please run the SQL script in client/src/utils/database/product_analysis_table.sql in your Supabase SQL editor first.');
        return;
      }
      
      console.log(`Analyzing ${productData.name} for ${targetMarket} market...`);
      if (competitorAnalysisNotes) {
        console.log(`User provided competitor notes: ${competitorAnalysisNotes}`);
      }
      
      // Use OpenAI service to get analysis
      let analysis;
      try {
        analysis = await analyzeProductDevelopmentPath(
          productData,
          targetMarket,
          competitorAnalysisNotes
        );
      } catch (openaiError) {
        console.error('OpenAI API error:', openaiError);
        
        // Fallback to mock data if OpenAI fails (e.g., API key not set)
        console.warn('Falling back to mock data due to OpenAI API error');
        analysis = {
          market_size: `$${(Math.random() * 10 + 5).toFixed(2)} billion USD`,
          competitor_products: "Several similar products exist in early development stages, including Product X by Company A and Product Y by Company B.",
          development_steps: [
            { step: "Proof of Concept", duration: "6-8 months", cost: "$1.2M - $1.5M", success_probability: "65%" },
            { step: "Preclinical Testing", duration: "12-18 months", cost: "$3.5M - $5M", success_probability: "48%" },
            { step: "IND Application", duration: "3-6 months", cost: "$300K - $500K", success_probability: "75%" },
            { step: "Phase I Clinical Trials", duration: "12-18 months", cost: "$5M - $8M", success_probability: "60%" },
            { step: "Phase II Clinical Trials", duration: "18-24 months", cost: "$10M - $15M", success_probability: "35%" },
            { step: "Phase III Clinical Trials", duration: "24-36 months", cost: "$20M - $30M", success_probability: "58%" },
            { step: "NDA Submission", duration: "6-12 months", cost: "$2M - $4M", success_probability: "85%" },
            { step: "FDA Review", duration: "6-10 months", cost: "$1M - $2M", success_probability: "80%" }
          ],
          total_development_time: "7-9 years",
          total_development_cost: "$43M - $66M USD",
          overall_success_probability: "8.6%",
          recommendations: "Focus on validating the core concept with minimal viable experiments. Consider partnering with academic institutions for initial proof of concept studies to reduce costs."
        };
      }
      
      // Save the analysis to database
      const { data, error: saveError } = await supabase
        .from('product_analysis')
        .upsert({
          product_id: id,
          user_id: user.id,
          analysis_data: analysis,
          market_size: analysis.market_size,
          competitor_products: analysis.competitor_products,
          total_development_time: analysis.total_development_time,
          total_development_cost: analysis.total_development_cost,
          overall_success_probability: analysis.overall_success_probability,
          created_at: existingAnalysis?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'product_id',
          ignoreDuplicates: false
        })
        .select();
      
      if (saveError) {
        // If we get a "relation does not exist" error, the table doesn't exist
        if (saveError.code === '42P01') {
          setTableExists(false);
          throw new Error('The product_analysis table does not exist in your database. Please run the SQL script provided in the error message.');
        }
        throw saveError;
      }
      
      setAnalysisResult(data[0]);
      
    } catch (err) {
      console.error('Error during analysis:', err);
      setError(`Failed to analyze product: ${err.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Handle next step navigation
  const handleNext = () => {
    navigate(`/products/${id}/financial`);
  };
  
  if (isLoading) {
    return <LoadingScreen message="Loading product data..." />;
  }
  
  if (!productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>Product not found. Please check the URL and try again.</p>
        </div>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to={`/products/${id}`} className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Product Details
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Product Development Path Analysis</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
          {!tableExists && (
            <div className="mt-4">
              <p>To create the product_analysis table, copy the SQL below and run it in your Supabase SQL Editor:</p>
              <div className="mt-2 p-4 bg-gray-800 text-white rounded overflow-auto max-h-96">
                <pre className="text-xs">
{`-- Create the product_analysis table
CREATE TABLE IF NOT EXISTS public.product_analysis (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    product_id UUID NOT NULL REFERENCES public.products(id) ON DELETE CASCADE,
    user_id UUID NOT NULL,
    analysis_data JSONB NOT NULL,
    market_size TEXT,
    competitor_products TEXT,
    total_development_time TEXT,
    total_development_cost TEXT,
    overall_success_probability TEXT,
    created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
    updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
);

-- Set up Row Level Security
ALTER TABLE public.product_analysis ENABLE ROW LEVEL SECURITY;

-- Create policy to allow users to view their own product analyses
CREATE POLICY "Users can view their own product analyses"
ON public.product_analysis
FOR SELECT
USING (auth.uid() = user_id);

-- Create policy to allow users to insert their own product analyses
CREATE POLICY "Users can insert their own product analyses"
ON public.product_analysis
FOR INSERT
WITH CHECK (auth.uid() = user_id);

-- Create policy to allow users to update their own product analyses
CREATE POLICY "Users can update their own product analyses"
ON public.product_analysis
FOR UPDATE
USING (auth.uid() = user_id);

-- Add indexes for better performance
CREATE INDEX IF NOT EXISTS product_analysis_product_id_idx ON public.product_analysis (product_id);
CREATE INDEX IF NOT EXISTS product_analysis_user_id_idx ON public.product_analysis (user_id);`}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
      
      {!analysisResult ? (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Analyze Development Path</h2>
            <p className="text-gray-600">
              For {productData.name} in the {productData.development_stage} stage, we'll analyze the potential development path
              including market size, competition, development steps, costs, and success probabilities.
            </p>
            
            {!tableExists && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-100 rounded-md">
                <p className="text-yellow-700">
                  <strong>Note:</strong> The product_analysis table does not exist in your database. 
                  The analysis can still be performed, but the results cannot be saved. Please see the error message above for instructions on creating the table.
                </p>
              </div>
            )}
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-2">Product Information</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Product Name:</p>
                  <p className="font-medium">{productData.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Category:</p>
                  <p className="font-medium">{productData.product_category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Development Stage:</p>
                  <p className="font-medium">{productData.development_stage}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <label htmlFor="targetMarket" className="block text-gray-700 text-sm font-medium mb-2">
              Target Market
            </label>
            <select
              id="targetMarket"
              value={targetMarket}
              onChange={(e) => setTargetMarket(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {marketOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <p className="text-gray-500 text-xs mt-1">
              Select the primary target market for your product.
            </p>
          </div>
          
          <div className="mb-6">
            <label htmlFor="competitorNotes" className="block text-gray-700 text-sm font-medium mb-2">
              Competitor Analysis Notes (Optional)
            </label>
            <textarea
              id="competitorNotes"
              value={competitorAnalysisNotes}
              onChange={(e) => setCompetitorAnalysisNotes(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Any known competitors or similar products..."
            ></textarea>
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleAnalyzeClick}
              className="btn-primary"
              disabled={isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Analyzing...
                </>
              ) : "Analyze Development Path"}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Analysis Results</h2>
            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
              Completed
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Market Size</h3>
              <p className="text-gray-600">{analysisResult.analysis_data.market_size}</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Total Development Time</h3>
              <p className="text-gray-600">{analysisResult.analysis_data.total_development_time}</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Total Development Cost</h3>
              <p className="text-gray-600">{analysisResult.analysis_data.total_development_cost}</p>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Overall Success Probability</h3>
              <p className="text-gray-600">{analysisResult.analysis_data.overall_success_probability}</p>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-2">Competitor Products</h3>
            <p className="text-gray-600">{analysisResult.analysis_data.competitor_products}</p>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-2">Development Steps</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Success Probability</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analysisResult.analysis_data.development_steps.map((step, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{step.step}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{step.duration}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{step.cost}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{step.success_probability}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-2">Recommendations</h3>
            <p className="text-gray-600">{analysisResult.analysis_data.recommendations}</p>
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleNext}
              className="btn-primary"
            >
              Continue to Financial Analysis
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAnalysisPage; 